import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SiteLanguageList_siteLanguages_edges_node } from "../../types/SiteLanguageList";

export interface SiteLanguageListProps extends ListProps, ListActions {
  siteLanguages: SiteLanguageList_siteLanguages_edges_node[];
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {},
      colActive: {
        width: 200
      }
    },
    colUrl: {},
    colName: {
      paddingLeft: 0
    },
    colActive: {},
    link: {
      cursor: "pointer"
    }
  }),
  { name: "SiteLanguageList" }
);

const numberOfColumns = 5;

const SiteLanguageList: React.FC<SiteLanguageListProps> = props => {
  const {
    settings,
    siteLanguages,
    disabled,
    onNextPage,
    pageInfo,
    onRowClick,
    onUpdateListSettings,
    onPreviousPage,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  return (
    <Card>
      <ResponsiveTable>
        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={siteLanguages}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCellHeader className={classes.colName}>
            <FormattedMessage
              defaultMessage="Name"
              description="dialog header"
            />
          </TableCellHeader>
          <TableCellHeader className={classes.colName}>
            <FormattedMessage
              defaultMessage="Code"
              description="dialog header"
            />
          </TableCellHeader>
          <TableCellHeader className={classes.colUrl}>
            <FormattedMessage
              defaultMessage="Domain"
              description="dialog header"
            />
          </TableCellHeader>
          <TableCellHeader className={classes.colActive}>
            <FormattedMessage
              defaultMessage="Status"
              description="site language status"
            />
          </TableCellHeader>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            siteLanguages,
            siteLanguage => {
              const isSelected = siteLanguage
                ? isChecked(siteLanguage.id)
                : false;

              return (
                <TableRow
                  hover={!!siteLanguage}
                  className={!!siteLanguage ? classes.link : undefined}
                  onClick={
                    siteLanguage ? onRowClick(siteLanguage.id) : undefined
                  }
                  key={siteLanguage ? siteLanguage.id : "skeleton"}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      disableClickPropagation
                      onChange={() => toggle(siteLanguage.id)}
                    />
                  </TableCell>
                  <TableCellHeader className={classes.colName}>
                    {maybe<React.ReactNode>(
                      () => siteLanguage.language,
                      <Skeleton />
                    )}
                  </TableCellHeader>
                  <TableCellHeader className={classes.colName}>
                    {maybe<React.ReactNode>(
                      () => siteLanguage.name,
                      <Skeleton />
                    )}
                  </TableCellHeader>
                  <TableCellHeader className={classes.colUrl}>
                    {maybe<React.ReactNode>(
                      () => (siteLanguage.domain ? siteLanguage.domain : "-"),
                      <Skeleton />
                    )}
                  </TableCellHeader>
                  <TableCellHeader className={classes.Active}>
                    {maybe<React.ReactNode>(
                      () => (
                        <StatusLabel
                          label={
                            siteLanguage.isActive
                              ? intl.formatMessage({
                                  defaultMessage: "Active",
                                  description: "site language status"
                                })
                              : intl.formatMessage({
                                  defaultMessage: "Inactive",
                                  description: "site language status"
                                })
                          }
                          status={siteLanguage.isActive ? "success" : "error"}
                        />
                      ),
                      <Skeleton />
                    )}
                  </TableCellHeader>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No pages found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </Card>
  );
};
SiteLanguageList.displayName = "SiteLanguageList";
export default SiteLanguageList;
